import React from 'react';

interface LanguageIconProps {
  language: string;
  size?: number;
  className?: string;
}

/**
 * Language icon component that displays programming language logos
 * Uses Simple Icons CDN for logos
 */
const LanguageIcon: React.FC<LanguageIconProps> = ({
  language,
  size = 24,
  className = '',
}) => {
  // Map common language names to Simple Icons names
  const languageMap: Record<string, string> = {
    python: 'python',
    javascript: 'javascript',
    typescript: 'typescript',
    java: 'java',
    cpp: 'cplusplus',
    'c++': 'cplusplus',
    csharp: 'csharp',
    'c#': 'csharp',
    go: 'go',
    rust: 'rust',
    ruby: 'ruby',
    php: 'php',
    swift: 'swift',
    kotlin: 'kotlin',
    scala: 'scala',
    r: 'r',
    html: 'html5',
    css: 'css3',
    react: 'react',
    vue: 'vue.js',
    angular: 'angular',
    nodejs: 'nodedotjs',
    'node.js': 'nodedotjs',
    docker: 'docker',
    kubernetes: 'kubernetes',
    aws: 'amazonaws',
    azure: 'microsoftazure',
    gcp: 'googlecloud',
    linux: 'linux',
    ubuntu: 'ubuntu',
    bash: 'gnubash',
    shell: 'gnubash',
    git: 'git',
    github: 'github',
    gitlab: 'gitlab',
    mysql: 'mysql',
    postgresql: 'postgresql',
    mongodb: 'mongodb',
    redis: 'redis',
    arduino: 'arduino',
  };

  // Normalize language name
  const normalizedLang = language.toLowerCase().trim();
  const iconName = languageMap[normalizedLang];

  if (!iconName) {
    // Fallback: try to use the language name directly (Simple Icons might have it)
    const fallbackName = normalizedLang.replace(/\s+/g, '');
    return (
      <img
        src={`https://cdn.simpleicons.org/${fallbackName}/4f46e5`}
        alt={language}
        width={size}
        height={size}
        className={className}
        onError={e => {
          // Hide image if it fails to load
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />
    );
  }

  return (
    <img
      src={`https://cdn.simpleicons.org/${iconName}/4f46e5`}
      alt={language}
      width={size}
      height={size}
      className={className}
      onError={e => {
        // Hide image if it fails to load
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  );
};

/**
 * Detect language from exercise title, category, or tags
 */
export const detectLanguage = (
  title?: string,
  category?: string,
  tags?: string[]
): string | null => {
  const searchText = [
    title?.toLowerCase() || '',
    category?.toLowerCase() || '',
    ...(tags || []).map(t => t.toLowerCase()),
  ].join(' ');

  const languages = [
    'python',
    'javascript',
    'typescript',
    'java',
    'cpp',
    'c++',
    'csharp',
    'c#',
    'go',
    'rust',
    'ruby',
    'php',
    'swift',
    'kotlin',
    'scala',
    'r',
    'html',
    'css',
    'react',
    'vue',
    'angular',
    'nodejs',
    'node.js',
    'docker',
    'kubernetes',
    'aws',
    'azure',
    'gcp',
    'linux',
    'ubuntu',
    'bash',
    'shell',
    'git',
    'github',
    'gitlab',
    'mysql',
    'postgresql',
    'mongodb',
    'redis',
    'arduino',
  ];

  for (const lang of languages) {
    if (searchText.includes(lang)) {
      return lang;
    }
  }

  return null;
};

export default LanguageIcon;
